import { ApiError, stopAgentTurn } from '../lib/api';

/**
 * The outcome the button reports.
 *
 * `nothing` is what `aborted: false` means and all it may claim: the rail cannot
 * tell a turn that just finished from one that never started from a runtime that
 * implements no stopping, so this must never read as "the agent does not support
 * this". `alreadyAsked` is the server declining to forward a stop it was just
 * asked for about this same agent here, and says so rather than borrowing either
 * `aborted` answer, since neither describes a request that was never made.
 * `failed` is every way an answer did not arrive — the plugin is gone, or it is
 * connected and refused the action, or it answered something unusable — so it may
 * claim only that the stop did not happen, never that the agent could not be
 * reached.
 */
export type StopOutcome = 'idle' | 'asking' | 'stopped' | 'nothing' | 'alreadyAsked' | 'failed';

const STOP_OUTCOME_LABEL: Record<StopOutcome, string> = {
  idle: 'Stop',
  asking: 'Stopping…',
  stopped: 'Stopped',
  nothing: 'Nothing to stop',
  alreadyAsked: 'Already asked to stop',
  failed: 'Could not stop',
};

/**
 * Asks one agent to stop the turn it is running in this channel.
 *
 * The outcome is held by whatever renders this, because a button is unmounted
 * every time its row goes away and the answer to a click — including one still in
 * flight — has to outlive that.
 */
export default function StopTurnButton({
  channelId,
  agentId,
  displayName,
  outcome,
  onOutcome,
  className,
}: {
  channelId: string;
  agentId: string;
  displayName: string;
  outcome: StopOutcome;
  onOutcome: (agentId: string, outcome: StopOutcome) => void;
  className: string;
}) {
  const asking = outcome === 'asking';

  // Only the request in flight closes the button. Every settled outcome is
  // clickable again: a request that failed, and a click that landed a moment
  // before the turn it meant to catch, are the two cases worth repeating, and
  // while the agent is still working this button is the only place to ask.
  //
  // Closed means aria-disabled and a refusal here, not the `disabled` attribute:
  // disabling a focused element hands focus to <body> and never gives it back,
  // which would end a keyboard user's interaction by losing their place in the
  // document.
  const ask = async () => {
    if (asking) return;
    onOutcome(agentId, 'asking');
    try {
      const { aborted } = await stopAgentTurn(channelId, agentId);
      onOutcome(agentId, aborted ? 'stopped' : 'nothing');
    } catch (err) {
      const throttled = err instanceof ApiError && err.status === 429;
      onOutcome(agentId, throttled ? 'alreadyAsked' : 'failed');
    }
  };

  // The accessible name carries the outcome and the visible label is contained
  // in it, so what the button says and what it is called stay the same control.
  // The name alone is not announced when it changes, so the outcome is also
  // spoken through a live region that is in the DOM from the first render.
  const name = `${STOP_OUTCOME_LABEL[outcome]}, ${displayName}`;
  return (
    <>
      <button
        type="button"
        className={className}
        onClick={ask}
        aria-disabled={asking}
        aria-label={name}
      >
        {STOP_OUTCOME_LABEL[outcome]}
      </button>
      <span className="sr-only" role="status">
        {outcome === 'idle' ? '' : name}
      </span>
    </>
  );
}
